import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'

export default function QueenElizabeth() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/queen-elizabeth-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Queen Elizabeth National Park
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            Uganda's Premier Wildlife Safari Destination
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Queen Elizabeth National Park stands as Uganda's most visited and diverse wildlife destination, offering
                an extraordinary blend of savannah, wetlands, forests, and crater lakes across 1,978 square kilometers.
                Home to 95 mammal species and over 600 bird species, this UNESCO Biosphere Reserve delivers classic
                African safari experiences with unique features like tree-climbing lions and boat cruises through hippo-filled channels.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The park's strategic location in western Uganda makes it the perfect centerpiece for multi-destination
                safaris, seamlessly connecting gorilla trekking in Bwindi, chimpanzee tracking in Kibale, and the dramatic
                Murchison Falls. With accommodation options ranging from budget camping to luxury eco-lodges,
                Queen Elizabeth provides accessible wildlife adventures for every traveler.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Size</h3>
                  <p className="text-gray-700">1,978 km²</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Mammal Species</h3>
                  <p className="text-gray-700">95 species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">600+ species</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Crater Lakes</h3>
                  <p className="text-gray-700">100+ lakes</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/queen-elizabeth-hero.jpg"
                alt="Queen Elizabeth National Park"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-12 md:py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-safari-green mb-8 md:mb-12 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Tree-Climbing Lions of Ishasha</h3>
              <p className="text-gray-700">
                The Ishasha sector hosts one of only two populations of tree-climbing lions worldwide. These
                magnificent cats regularly lounge in fig and acacia trees, possibly escaping ground insects or gaining
                elevated hunting vantage points.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Kazinga Channel Boat Cruise</h3>
              <p className="text-gray-700">
                This 32-kilometer natural waterway connecting Lakes Edward and George provides Uganda's highest
                wildlife concentration along its banks. Two-hour cruises reveal massive hippo pods and basking crocodiles.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Kasenyi Plains Game Viewing</h3>
              <p className="text-gray-700">
                These expansive savannah grasslands offer classic African safari experiences with high lion density,
                large buffalo herds, elephant families, and abundant Uganda kob antelope.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Kyambura Gorge Chimpanzee Tracking</h3>
              <p className="text-gray-700">
                This 100-meter deep forested gorge creates an "underground rainforest" environment harboring
                habituated chimpanzee groups. Daily tracking expeditions offer intimate primate encounters.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Crater Lakes Exploration</h3>
              <p className="text-gray-700">
                Over 100 volcanic crater lakes dot the landscape, many hosting flamingo populations and providing
                scenic photography opportunities. Lake Katwe features traditional salt mining operations.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h3 className="text-lg font-bold text-safari-green mb-2">Maramagambo Forest</h3>
              <p className="text-gray-700">
                This remaining natural forest offers guided walking safaris through diverse habitats supporting
                forest primates, bird species, and unique flora. Bat caves and python encounters enhance the experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  By Road from Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Via Mbarara (6-7 hours):</strong> 410 km through Uganda's southwest. Pass through Fort Portal for scenic mountain views and tea plantations.</p>
                  <p><strong>Via Kasese (5-6 hours):</strong> Alternative route through western region.</p>
                  <p className="text-sm italic">Well-maintained tarmac roads. 4WD recommended for park tracks during wet season.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-plane" className="w-6 h-6" />
                  By Air
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Mweya Airstrip:</strong> Daily scheduled and charter flights from Entebbe (1.5 hours). Located in the heart of the park.</p>
                  <p><strong>Kasese Airstrip:</strong> Alternative option near park northern gate.</p>
                  <p className="text-sm italic">Charter flights available for groups. Combine with other western Uganda parks.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-route" className="w-6 h-6" />
                  Multi-Park Circuit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>From Bwindi:</strong> 4-5 hours via scenic mountain roads. Perfect gorilla trek combination.</p>
                  <p><strong>From Kibale:</strong> 2-3 hours. Ideal for chimp-to-wildlife safari circuit.</p>
                  <p className="text-sm italic">Most popular as part of western Uganda circuit combining primates and safari wildlife.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-paw" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Lion Tracking Experience</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Join researchers to track collared lions using telemetry equipment. Monitor lion movements and behavior in this unique research-based safari experience.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $100 per person</p>
                <Link to="/activities/lion-tracking" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Kyambura Gorge Chimp Tracking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Descend into the dramatic "Valley of Apes"—a sunken forest gorge. Steep entry and exit make this a challenging but rewarding primate encounter.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Strenuous</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $100 per person</p>
                <Link to="/activities/chimp-kyambura-gorge" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-car" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Tree-Climbing Lions Safari</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Game drives in Ishasha sector to spot the famous tree-climbing lions lounging in fig trees. Also see elephants, buffalos, and Uganda kob.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-5 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $80 per person</p>
                <Link to="/activities/queen-elizabeth-game-drives" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-ship" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Kazinga Channel Boat Safari</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Cruise the channel connecting Lakes Edward and George. See massive hippo pods, Nile crocodiles, and over 600 bird species along the banks.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $30 per person</p>
                <Link to="/activities/kazinga-boat-safari" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-hot-air-balloon" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Hot Air Balloon Safari</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Dawn flight over the savanna for aerial wildlife viewing. Followed by a champagne bush breakfast. An unforgettable luxury experience.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 3-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $380 per person</p>
                <Link to="/activities/hot-air-balloon-safari" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-hiking" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Crater Lake Hikes</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Guided hikes around dramatic explosion craters with salt lakes. Stunning views and excellent birding in this volcanic landscape.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $40 per person</p>
                <Link to="/activities/crater-lakes-drive" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Dry Seasons (June-September, December-February):</strong> Best for game viewing as animals congregate around water sources. Easier road access.</p>
                  <p><strong>Wet Seasons (March-May, October-November):</strong> Lush scenery, excellent birding, fewer tourists. Some tracks may be challenging.</p>
                  <p className="text-sm italic">Year-round destination. Plan 2-3 days minimum for full park experience.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Neutral-colored clothing, sun hat, sunscreen, binoculars, camera with zoom lens</p>
                  <p><strong>Recommended:</strong> Insect repellent, light jacket for early morning drives, comfortable walking shoes</p>
                  <p><strong>Optional:</strong> Field guide books, journal, portable charger for devices</p>
                  <p className="text-sm italic">Morning game drives can be cool. Layer clothing for comfort.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-lightbulb" className="w-6 h-6" />
                  Insider Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Tree-Climbing Lions:</strong> Best spotted in Ishasha sector during midday heat when they rest in trees.</p>
                  <p><strong>Boat Cruise:</strong> Afternoon cruises often better for photography with ideal lighting conditions.</p>
                  <p><strong>Lion Tracking:</strong> Book in advance—limited slots available. Most successful in morning hours.</p>
                  <p><strong>Combo Tip:</strong> Combine with Kibale Forest for chimp trekking (2-3 hours away).</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Park Entry:</strong> Foreign adults $40/day. Valid 24 hours from entry time.</p>
                  <p><strong>Accommodation:</strong> Budget camping to luxury lodges ($50-$400+ per night).</p>
                  <p><strong>Activities:</strong> Budget $30-$400 per activity depending on type. Multi-day packages offer savings.</p>
                  <p><strong>Duration:</strong> Minimum 2 nights recommended to experience park diversity.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation ranges from budget campsites to luxury safari lodges, with options in different park sectors for varied experiences.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Mweya Safari Lodge</td>
                  <td className="px-6 py-4">Mweya Peninsula</td>
                  <td className="px-6 py-4">Premium rooms, infinity pool, Kazinga Channel views, fine dining</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Kyambura Gorge Lodge</td>
                  <td className="px-6 py-4">Kyambura Gorge edge</td>
                  <td className="px-6 py-4">Eco-luxury bandas, gorge views, spa, community-focused</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Bush Lodge</td>
                  <td className="px-6 py-4">Near Mweya</td>
                  <td className="px-6 py-4">Comfortable tents, restaurant, good value, central location</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Ishasha Jungle Lodge</td>
                  <td className="px-6 py-4">Ishasha sector</td>
                  <td className="px-6 py-4">River-front tents, tree-climbing lion territory, rustic charm</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Simba Safari Camp</td>
                  <td className="px-6 py-4">Kasenyi area</td>
                  <td className="px-6 py-4">Budget tents and bandas, basic facilities, communal dining</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">UWA Bandas</td>
                  <td className="px-6 py-4">Various park locations</td>
                  <td className="px-6 py-4">Basic self-catering bandas, camping available, very affordable</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What are the chances of seeing tree-climbing lions?</h3>
                <p className="text-gray-700">
                  In Ishasha sector, success rates are around 70-80% during midday hours when lions rest in fig trees to escape heat and ground insects. Morning and late afternoon sightings are less common.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How long is the Kazinga Channel boat cruise?</h3>
                <p className="text-gray-700">
                  Standard cruises last 2-3 hours, departing twice daily (morning and afternoon). Private charters available for longer excursions. Afternoon cruises offer better photography lighting.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I do chimp tracking here?</h3>
                <p className="text-gray-700">
                  Yes, in Kyambura Gorge. Success rates around 70% as chimps are semi-habituated. More challenging terrain than Kibale but fewer tourists. Permits $100 per person.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Which sector should I visit first?</h3>
                <p className="text-gray-700">
                  Mweya/Kasenyi for general game viewing and boat cruise. Ishasha for tree-climbing lions (4 hours south). Most visitors spend time in both sectors over 2-3 days.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is lion tracking worth the extra cost?</h3>
                <p className="text-gray-700">
                  Absolutely! This research-based experience lets you track collared lions with scientists using telemetry. Near-100% success rate and unique insights into lion behavior and conservation.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can Queen Elizabeth be combined with other parks?</h3>
                <p className="text-gray-700">
                  Perfect for multi-park circuits. Kibale Forest is 2-3 hours north (chimps), Bwindi is 4-5 hours south (gorillas). Most travelers combine 2-3 western Uganda parks in one trip.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Are there rhinos in the park?</h3>
                <p className="text-gray-700">
                  No, rhinos were poached to extinction here in the 1980s. The park has 4 of the Big Five: lions, leopards, elephants, and buffaloes. Visit Ziwa Rhino Sanctuary for rhino tracking.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is malaria prophylaxis necessary?</h3>
                <p className="text-gray-700">
                  Yes, recommended. Queen Elizabeth is in a malaria zone. Take prophylaxis, use insect repellent, and sleep under mosquito nets (provided by most lodges).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Experience Uganda's Premier Safari Destination
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Discover the incredible diversity of Queen Elizabeth National Park, from tree-climbing lions to
            hippo-filled channels. Our expert guides will lead you on unforgettable wildlife encounters in one of
            Africa's most spectacular safari destinations.
          </p>
          <button
            onClick={() => handleBooking('Complete Queen Elizabeth Experience')}
            className="btn-primary text-lg"
          >
            Plan Your Queen Elizabeth Adventure
          </button>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}